import { Text } from '@acies/core';
import theme from './theme';

/** The standard one line Text display Component that is used inside various Components. */
export default class Label extends Text {
	public static override observedAttributes = [...Text.observedAttributes, 'size', 'variant', 'text', 'truncate'];
	/** The size property combines the label fontSize and lineHeight under one simple t-shirt convention name. */
	public set size(value: 'large' | 'medium' | 'small' | 'xsmall') {
		if (this.#size === value) {
			return;
		}
		assertIsValidSize(value);
		this.#size = value;
		this.#sizeChanged = true;
		this.invalidateProperties();
	}

	public get size(): 'large' | 'medium' | 'small' | 'xsmall' {
		return this.#size;
	}

	/**
	 * The variant property controls which Label styles you want to apply to the Label.
	 *
	 * Dark, light, default, error or disabled
	 */
	public set variant(value: 'dark' | 'default' | 'disabled' | 'error' | 'light') {
		if (this.#variant === value) {
			return;
		}
		assertIsValidVariant(value);
		this.#variant = value;
		this.#variantChanged = true;
		this.invalidateProperties();
	}

	public get variant(): 'dark' | 'default' | 'disabled' | 'error' | 'light' {
		return this.#variant;
	}

	#size: 'large' | 'medium' | 'small' | 'xsmall';
	#sizeChanged: boolean;
	#variant: 'dark' | 'default' | 'disabled' | 'error' | 'light';
	#variantChanged: boolean;

	public constructor() {
		super();
		this.truncate = true;
		this.fontFamily = 'Inter';
		this.fontWeight = 500;
		this.fontSize = 14;
		this.lineHeight = 20;
		this.fill = theme.colors.text.sub;
		this.slashedZero = true;
		this.tabularFigures = true;
		this.maxLines = 1;
		this.#variant = 'default';
		this.#variantChanged = false;
		this.#size = 'medium';
		this.#sizeChanged = false;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'size') {
			this.#sizeAttributeChanged(value);
		} else if (name === 'variant') {
			this.#variantAttributeChanged(value);
		} else if (name === 'text') {
			this.#textAttributeChanged(value);
		} else if (name === 'truncate') {
			this.#truncateAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#variantChanged) {
			this.#commitVariant();
		}
		if (this.#sizeChanged) {
			this.#commitSize();
		}
	}

	#commitSize(): void {
		this.#sizeChanged = false;
		if (this.size === 'xsmall') {
			this.fontSize = 10;
			this.lineHeight = 12;
		} else if (this.size === 'small') {
			this.fontSize = 12;
			this.lineHeight = 16;
		} else if (this.size === 'medium') {
			this.fontSize = 14;
			this.lineHeight = 20;
		} else if (this.size === 'large') {
			this.fontSize = 16;
			this.lineHeight = 24;
		}
	}

	#commitVariant(): void {
		this.#variantChanged = false;
		if (this.variant === 'dark') {
			this.fill = theme.colors.text.strong;
			this.fontWeight = 500;
		} else if (this.variant === 'default') {
			this.fill = theme.colors.text.sub;
			this.fontWeight = 500;
		} else if (this.variant === 'disabled') {
			this.fill = theme.colors.text.disabled;
			this.fontWeight = 500;
		} else if (this.variant === 'error') {
			this.fill = theme.colors.text.error;
			this.fontWeight = 500;
		} else if (this.variant === 'light') {
			this.fill = theme.colors.text.sub;
			this.fontWeight = 400;
		}
	}

	#sizeAttributeChanged(value: string): void {
		assertIsValidSize(value);
		this.size = value;
	}

	#textAttributeChanged(value: string): void {
		this.text = value;
	}

	#truncateAttributeChanged(value: string): void {
		this.truncate = value === 'true';
	}

	#variantAttributeChanged(value: string): void {
		assertIsValidVariant(value);
		this.variant = value;
	}
}
customElements.define('ac-label', Label);

function assertIsValidSize(value: unknown): asserts value is 'large' | 'medium' | 'small' | 'xsmall' {
	if (value === 'large' || value === 'medium' || value === 'small' || value === 'xsmall') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "small", "medium", "large" or "xsmall"`);
}

function assertIsValidVariant(value: unknown): asserts value is 'dark' | 'default' | 'disabled' | 'error' | 'light' {
	if (value === 'dark' || value === 'default' || value === 'disabled' || value === 'error' || value === 'light') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "dark", "default", "disabled", "error" or "light"`);
}
